import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Wallet, Plus, TrendingDown, RefreshCw, AlertTriangle, CheckCircle,
  Clock, ArrowUpRight, ArrowDownLeft, ChevronRight, Zap, Calendar,
  BarChart3, Package, Info, Sparkles, ShieldCheck, ArrowUp, ArrowDown,
  Star
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import MerchantLayout from '@/components/layouts/MerchantLayout';

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ Authorization: `Bearer ${sessionStorage.getItem('token') || localStorage.getItem('token')}` });
const fmt = (n, dec = 6) => parseFloat((n || 0).toFixed(dec));
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-US', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

/* ── Plan badge colour ── */
const planColor = (name = '') => {
  const n = name.toLowerCase();
  if (n.includes('business') || n.includes('enterprise')) return 'from-amber-500 to-orange-500';
  if (n.includes('pro') || n.includes('growth')) return 'from-violet-500 to-purple-600';
  if (n.includes('starter')) return 'from-blue-500 to-indigo-600';
  return 'from-slate-500 to-slate-600'; // free
};

/* ──────────────────────────────────────────────────────── */
/* Current Plan Hero                                        */
/* ──────────────────────────────────────────────────────── */
function PlanHero({ subscription, planFees, inTrial, trialDaysLeft, trialEnd, onActivateEarly, onSwitchPlan }) {
  const planName = subscription?.plan_name || 'Free';
  const isPaid = (planFees?.monthly_price || 0) > 0;

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden">
      {/* Gradient header */}
      <div className={`bg-gradient-to-r ${planColor(planName)} p-5`}>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-white/70 text-xs font-medium uppercase tracking-widest mb-1">Current Plan</p>
            <div className="flex items-center gap-2">
              <h2 data-testid="current-plan-name" className="text-3xl font-bold text-white">{planName}</h2>
              {inTrial && (
                <span className="bg-white/20 text-white text-xs font-semibold px-2 py-0.5 rounded-full border border-white/30">
                  Trial
                </span>
              )}
            </div>
            <p className="text-white/70 text-sm mt-1">
              {inTrial
                ? `Trial ends ${fmtDate(trialEnd)} — ${trialDaysLeft} day${trialDaysLeft !== 1 ? 's' : ''} remaining`
                : subscription?.next_billing_date
                  ? `Renews on ${fmtDate(subscription.next_billing_date)}`
                  : 'No renewal — free forever'}
            </p>
          </div>
          <div className="flex flex-col gap-2 items-end">
            {inTrial && isPaid && (
              <Button
                data-testid="activate-plan-early-btn"
                size="sm"
                onClick={onActivateEarly}
                className="bg-white text-slate-900 hover:bg-white/90 font-semibold text-xs gap-1.5"
              >
                <Sparkles className="h-3.5 w-3.5" /> Activate Now
              </Button>
            )}
            <Button
              data-testid="change-plan-btn"
              size="sm"
              variant="outline"
              onClick={onSwitchPlan}
              className="border-white/40 text-white hover:bg-white/10 text-xs gap-1.5"
            >
              <ArrowUp className="h-3.5 w-3.5" /> Change Plan
            </Button>
          </div>
        </div>
      </div>

      {/* Fee metrics row */}
      <div className="grid grid-cols-3 divide-x divide-border">
        <div className="p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">% Per TX</p>
          <p data-testid="plan-percent-fee" className="text-lg font-bold text-foreground">
            {inTrial ? <span className="text-blue-400 text-sm font-semibold">Free (Trial)</span> : `${planFees?.percent_fee ?? 0}%`}
          </p>
        </div>
        <div className="p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">Monthly</p>
          <p className="text-lg font-bold text-foreground">
            {inTrial ? <span className="text-blue-400 text-sm font-semibold">Free (Trial)</span> : `$${planFees?.monthly_price ?? 0}`}
          </p>
        </div>
        <div className="p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">TX Limit</p>
          <p className="text-lg font-bold text-foreground">
            {planFees?.tx_limit ? `${planFees.tx_limit}/mo` : '∞'}
          </p>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────── */
/* Balance Card                                             */
/* ──────────────────────────────────────────────────────── */
function BalanceCard({ balance, planExpired, graceEnd, inTrial, signupBonusPending, onAddBalance }) {
  const low = balance < 10 && balance > 0;
  const empty = balance <= 0;
  return (
    <div className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-start justify-between mb-4">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">Account Balance</p>
          <div className="flex items-end gap-2">
            <span
              data-testid="account-balance"
              className={`text-5xl font-bold tabular-nums ${empty ? 'text-red-400' : low ? 'text-amber-400' : 'text-emerald-400'}`}
            >
              ${fmt(balance, 2)}
            </span>
            <span className="text-muted-foreground mb-1.5 text-sm">USDT</span>
          </div>
          {signupBonusPending > 0 && (
            <p className="text-xs text-amber-400 mt-1.5 flex items-center gap-1">
              <Info className="h-3 w-3" /> ${signupBonusPending} signup bonus — reclaimed on first top-up
            </p>
          )}
        </div>
        <Button
          data-testid="add-balance-btn"
          onClick={onAddBalance}
          className="bg-emerald-500 hover:bg-emerald-600 text-white gap-2"
        >
          <Plus className="h-4 w-4" /> Add Balance
        </Button>
      </div>

      {planExpired && (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>Your plan has expired. Invoice creation is blocked. Top up your balance to reactivate.</span>
        </div>
      )}
      {!planExpired && graceEnd && (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400 text-sm">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>Insufficient balance for plan renewal. Grace period ends <strong>{fmtDate(graceEnd)}</strong>. Top up to avoid invoice blocking.</span>
        </div>
      )}
      {!planExpired && !graceEnd && !inTrial && empty && (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400 text-sm">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>Balance is empty. New invoice creation is blocked until you add funds.</span>
        </div>
      )}
      {!planExpired && !graceEnd && !inTrial && low && (
        <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400 text-sm">
          <Info className="h-4 w-4 mt-0.5 shrink-0" />
          <span>Balance is low. Consider adding funds soon.</span>
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────── */
/* Add Balance Modal                                        */
/* ──────────────────────────────────────────────────────── */
function AddBalanceModal({ open, onClose, onSuccess }) {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const presets = [10, 25, 50, 100, 250];

  const handlePay = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt < 10) { toast.error('Minimum top-up is $10 USDT'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/billing/add-balance`, { amount: amt }, { headers: getHeaders() });
      toast.success('Redirecting to checkout...');
      window.open(data.checkout_url, '_blank');
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to initiate top-up');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5 text-emerald-400" /> Add Balance
          </DialogTitle>
          <DialogDescription>Top up your prepaid account balance with USDT. Minimum $10.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="flex flex-wrap gap-2">
            {presets.map(p => (
              <button key={p} onClick={() => setAmount(String(p))}
                className={`px-4 py-1.5 rounded-lg text-sm font-medium border transition-colors ${amount === String(p) ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' : 'border-border hover:border-emerald-500/50 text-muted-foreground'}`}>
                ${p}
              </button>
            ))}
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Custom Amount (USDT)</label>
            <Input data-testid="topup-amount-input" type="number" min="10" value={amount}
              onChange={e => setAmount(e.target.value)} placeholder="Enter amount (min $10)" />
          </div>
          <div className="p-3 rounded-lg bg-muted/50 text-xs text-muted-foreground space-y-1">
            <p className="flex items-center gap-1.5"><CheckCircle className="h-3.5 w-3.5 text-emerald-400" />Payment via CoinPayUp — USDT on BSC/TRC20</p>
            <p className="flex items-center gap-1.5"><CheckCircle className="h-3.5 w-3.5 text-emerald-400" />Balance credited instantly after confirmation</p>
            <p className="flex items-center gap-1.5"><Info className="h-3.5 w-3.5 text-amber-400" />Minimum top-up: $10 USDT</p>
          </div>
          <Button data-testid="confirm-topup-btn" onClick={handlePay}
            disabled={loading || !amount || parseFloat(amount) < 10}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-white">
            {loading ? 'Processing...' : `Pay $${amount || '0'} USDT`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ──────────────────────────────────────────────────────── */
/* Activate Trial Early Modal                               */
/* ──────────────────────────────────────────────────────── */
function ActivateEarlyModal({ open, onClose, plan, balance, onSuccess, onAddBalance }) {
  const [loading, setLoading] = useState(false);
  const monthly = plan?.monthly_price ?? 0;
  const canCharge = monthly === 0 || balance >= monthly;
  const shortfall = monthly > 0 ? Math.max(0, monthly - balance) : 0;

  const handleActivate = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/api/billing/activate-trial-early`, {}, { headers: getHeaders() });
      toast.success(`${plan?.name} plan activated!`);
      onSuccess?.();
      onClose();
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error('Insufficient balance. Please top up first.');
      } else {
        toast.error(err.response?.data?.detail || 'Failed to activate plan');
      }
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-400" /> Activate Plan Early
          </DialogTitle>
          <DialogDescription>End your free trial now and start your {plan?.name} plan immediately.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-1">
          <div className="rounded-xl border border-border bg-muted/40 p-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Plan</span><span className="font-semibold">{plan?.name}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Monthly fee</span><span className="font-semibold">${monthly}/mo</span></div>
            <div className="flex justify-between border-t border-border pt-2">
              <span className="text-muted-foreground">Your balance</span>
              <span className={`font-semibold ${canCharge ? 'text-emerald-400' : 'text-red-400'}`}>${fmt(balance, 2)} USDT</span>
            </div>
          </div>

          {!canCharge && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 space-y-2">
              <p className="text-xs text-red-400 font-semibold flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> Insufficient balance — cannot activate
              </p>
              <p className="text-xs text-muted-foreground">
                You need <span className="font-semibold text-foreground">${monthly} USDT</span> to activate {plan?.name}.
                Please top up <span className="font-semibold text-foreground">${shortfall.toFixed(2)} USDT</span> or more to continue.
              </p>
              <Button
                data-testid="topup-from-activate-btn"
                size="sm"
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white text-xs gap-1.5"
                onClick={() => { onClose(); onAddBalance?.(); }}
              >
                <Plus className="h-3.5 w-3.5" /> Add Balance
              </Button>
            </div>
          )}
          {canCharge && monthly > 0 && (
            <p className="text-xs text-emerald-400 flex items-center gap-1.5">
              <CheckCircle className="h-3.5 w-3.5" /> ${monthly} USDT will be deducted from your balance immediately.
            </p>
          )}

          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button
              data-testid="confirm-activate-early-btn"
              onClick={handleActivate}
              disabled={loading || !canCharge}
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-white disabled:opacity-50"
            >
              {loading ? 'Activating...' : 'Activate Now'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ──────────────────────────────────────────────────────── */
/* Switch Plan Modal                                        */
/* ──────────────────────────────────────────────────────── */
function SwitchPlanModal({ open, onClose, plans, currentPlanId, preselectedPlanId, balance, onSuccess, onAddBalance }) {
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('select'); // 'select' | 'confirm'

  useEffect(() => {
    if (!open) { setSelected(null); setStep('select'); }
    else if (preselectedPlanId) { setSelected(preselectedPlanId); setStep('confirm'); }
  }, [open, preselectedPlanId]);

  const newPlan = plans.find(p => p.id === selected);
  const monthly = newPlan?.monthly_price ?? 0;
  const canCharge = balance >= monthly;

  const handleSwitch = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/billing/change-plan`, { plan_id: selected }, { headers: getHeaders() });
      if (data.status === 'already_on_plan') {
        toast.info(`You're already on the ${data.plan_name} plan.`);
      } else {
        toast.success(data.charged > 0
          ? `Switched to ${data.plan_name}! $${data.charged} charged from balance.`
          : `Switched to ${data.plan_name}!`
        );
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      // 402 = insufficient balance — show inline, not toast
      const msg = err.response?.data?.detail || 'Failed to switch plan';
      if (err.response?.status === 402) {
        toast.error('Insufficient balance. Please top up first.');
      } else {
        toast.error(msg);
      }
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="h-5 w-5 text-primary" /> Change Plan
          </DialogTitle>
          <DialogDescription>Choose a plan. Paid plans are charged from your balance immediately.</DialogDescription>
        </DialogHeader>

        {step === 'select' && (
          <div className="space-y-3 pt-1">
            {plans.map(plan => {
              const isCurrent = plan.id === currentPlanId;
              const isSelected = plan.id === selected;
              return (
                <button
                  key={plan.id}
                  data-testid={`plan-option-${plan.id}`}
                  onClick={() => !isCurrent && setSelected(plan.id)}
                  disabled={isCurrent}
                  className={`w-full text-left p-4 rounded-xl border transition-all ${
                    isCurrent ? 'border-border bg-muted/30 opacity-60 cursor-not-allowed' :
                    isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 bg-card cursor-pointer'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="font-semibold text-foreground flex items-center gap-2">
                        {plan.name}
                        {isCurrent && <Badge variant="outline" className="text-[10px] px-1.5">Current</Badge>}
                        {isSelected && <CheckCircle className="h-4 w-4 text-primary" />}
                      </span>
                      {plan.description && <p className="text-xs text-muted-foreground mt-0.5">{plan.description}</p>}
                    </div>
                    <div className="text-right shrink-0 ml-4">
                      <p className="font-bold text-foreground">${plan.monthly_price ?? 0}<span className="text-xs font-normal text-muted-foreground">/mo</span></p>
                      <p className="text-xs text-muted-foreground">{plan.percent_fee ?? 0}% per TX</p>
                    </div>
                  </div>
                </button>
              );
            })}
            <div className="flex gap-2 pt-1">
              <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
              <Button
                data-testid="plan-select-next-btn"
                className="flex-1"
                disabled={!selected}
                onClick={() => setStep('confirm')}
              >
                Continue
              </Button>
            </div>
          </div>
        )}

        {step === 'confirm' && newPlan && (
          <div className="space-y-4 pt-1">
            <div className="rounded-xl border border-border bg-muted/40 p-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">New plan</span><span className="font-semibold">{newPlan.name}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Monthly fee</span><span className="font-semibold">${monthly}/mo</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">% Per TX</span><span className="font-semibold">{newPlan.percent_fee ?? 0}%</span></div>
              {newPlan.tx_limit && <div className="flex justify-between"><span className="text-muted-foreground">TX Limit</span><span className="font-semibold">{newPlan.tx_limit}/mo</span></div>}
              <div className="border-t border-border pt-2 flex justify-between">
                <span className="text-muted-foreground">Your balance</span>
                <span className={`font-semibold ${canCharge || monthly === 0 ? 'text-emerald-400' : 'text-red-400'}`}>${fmt(balance, 2)} USDT</span>
              </div>
            </div>

            {monthly > 0 && !canCharge && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 space-y-2">
                <p className="text-xs text-red-400 font-semibold flex items-center gap-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  Insufficient balance — plan cannot be changed
                </p>
                <p className="text-xs text-muted-foreground">
                  You need <span className="font-semibold text-foreground">${monthly} USDT</span> to activate {newPlan.name}. 
                  Your balance is <span className="font-semibold text-red-400">${fmt(balance, 2)} USDT</span>.
                  Please top up <span className="font-semibold text-foreground">${(monthly - balance).toFixed(2)} USDT</span> or more to continue.
                </p>
                <Button
                  data-testid="topup-from-switch-btn"
                  size="sm"
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white text-xs gap-1.5 mt-1"
                  onClick={() => { onClose(); onAddBalance?.(); }}
                >
                  <Plus className="h-3.5 w-3.5" /> Add Balance
                </Button>
              </div>
            )}
            {monthly > 0 && canCharge && (
              <div className="space-y-2">
                <p className="text-xs text-emerald-400 flex items-center gap-1.5">
                  <CheckCircle className="h-3.5 w-3.5" /> ${monthly} USDT will be deducted from your balance immediately.
                </p>
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <Info className="h-3.5 w-3.5 shrink-0 text-blue-400" />
                  The monthly plan fee of <span className="font-semibold text-foreground mx-0.5">${monthly} USDT</span> will be automatically charged from your account balance on each renewal date.
                </p>
              </div>
            )}
            {monthly === 0 && (
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <CheckCircle className="h-3.5 w-3.5 text-emerald-400" /> Free plan — no charge.
              </p>
            )}

            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setStep('select')}>Back</Button>
              <Button
                data-testid="confirm-switch-plan-btn"
                onClick={handleSwitch}
                disabled={loading || (monthly > 0 && !canCharge)}
                className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50"
              >
                {loading ? 'Switching...' : `Switch to ${newPlan.name}`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ──────────────────────────────────────────────────────── */
/* Transaction Row                                          */
/* ──────────────────────────────────────────────────────── */
function TransactionRow({ tx }) {
  const isCredit = tx.amount > 0;
  const typeLabels = {
    topup:          { label: 'Top-Up',       color: 'text-emerald-400 bg-emerald-500/10' },
    charge_tx_fee:  { label: 'TX Fee',        color: 'text-amber-400 bg-amber-500/10' },
    charge_monthly: { label: 'Monthly Plan',  color: 'text-blue-400 bg-blue-500/10' },
    charge_extra_tx:{ label: 'Extra TX',      color: 'text-purple-400 bg-purple-500/10' },
    signup_bonus:   { label: 'Signup Bonus',  color: 'text-amber-400 bg-amber-500/10' },
    refund:         { label: 'Refund',        color: 'text-green-400 bg-green-500/10' },
  };
  const t = typeLabels[tx.type] || { label: tx.type, color: 'text-gray-400 bg-gray-500/10' };
  return (
    <tr className="border-b border-border/50 hover:bg-muted/30 transition-colors">
      <td className="py-3 px-4">
        <span className={`inline-flex items-center text-xs font-medium px-2 py-1 rounded-full ${t.color}`}>{t.label}</span>
      </td>
      <td className="py-3 px-4 text-sm text-muted-foreground max-w-xs truncate">{tx.description}</td>
      <td className="py-3 px-4 text-sm text-muted-foreground">{fmtDateTime(tx.created_at)}</td>
      <td className="py-3 px-4 text-right">
        <span className={`font-semibold tabular-nums text-sm ${isCredit ? 'text-emerald-400' : 'text-red-400'}`}>
          {isCredit ? '+' : ''}{fmt(tx.amount, 4)} USDT
        </span>
      </td>
      <td className="py-3 px-4 text-right text-xs text-muted-foreground tabular-nums">${fmt(tx.balance_after, 4)}</td>
    </tr>
  );
}

/* ──────────────────────────────────────────────────────── */
/* Plan Card (Plans Tab)                                    */
/* ──────────────────────────────────────────────────────── */
function PlanCard({ plan, isCurrent, inTrial, onSwitch }) {
  const isPaid = (plan.monthly_price || 0) > 0;
  return (
    <div className={`rounded-xl border p-5 space-y-4 flex flex-col ${isCurrent ? 'border-primary/50 bg-primary/5 ring-1 ring-primary/20' : 'border-border bg-card'}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <h3 className="font-bold text-foreground text-lg">{plan.name}</h3>
            {isCurrent && <Badge className="bg-primary/20 text-primary text-[10px] px-1.5">Current</Badge>}
          </div>
          {plan.description && <p className="text-xs text-muted-foreground">{plan.description}</p>}
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-foreground">${plan.monthly_price ?? 0}</p>
          <p className="text-xs text-muted-foreground">/month</p>
        </div>
      </div>
      <div className="space-y-1.5 text-sm flex-1">
        <div className="flex justify-between py-1 border-b border-border/50">
          <span className="text-muted-foreground">% Per TX</span>
          <span className="font-semibold">{plan.percent_fee ?? 0}%</span>
        </div>
        <div className="flex justify-between py-1 border-b border-border/50">
          <span className="text-muted-foreground">TX Limit</span>
          <span className="font-semibold">{plan.tx_limit ? `${plan.tx_limit}/mo` : 'Unlimited'}</span>
        </div>
        {plan.extra_tx_cost > 0 && (
          <div className="flex justify-between py-1">
            <span className="text-muted-foreground">Extra TX</span>
            <span className="font-semibold">${plan.extra_tx_cost}/tx</span>
          </div>
        )}
      </div>
      {isCurrent && !inTrial && (
        <div className="flex items-center gap-1.5 text-xs text-primary font-medium">
          <ShieldCheck className="h-3.5 w-3.5" /> Active Plan
        </div>
      )}
      {isCurrent && inTrial && (
        <div className="flex items-center gap-1.5 text-xs text-blue-400 font-medium">
          <Clock className="h-3.5 w-3.5" /> Free Trial Active
        </div>
      )}
      {!isCurrent && (
        <Button
          data-testid={`switch-to-${plan.id}-btn`}
          variant="outline"
          size="sm"
          className="w-full text-xs gap-1.5"
          onClick={() => onSwitch(plan.id)}
        >
          {isPaid ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />}
          Switch to {plan.name}
        </Button>
      )}
    </div>
  );
}

/* ──────────────────────────────────────────────────────── */
/* Main Page                                                */
/* ──────────────────────────────────────────────────────── */
export default function MerchantBilling() {
  const [balanceData, setBalanceData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [txTotal, setTxTotal] = useState(0);
  const [feeLedger, setFeeLedger] = useState([]);
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addBalanceOpen, setAddBalanceOpen] = useState(false);
  const [activateEarlyOpen, setActivateEarlyOpen] = useState(false);
  const [switchPlanOpen, setSwitchPlanOpen] = useState(false);
  const [preselectedPlanId, setPreselectedPlanId] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [txPage, setTxPage] = useState(1);

  const fetchBalance  = useCallback(async () => {
    const { data } = await axios.get(`${API}/api/billing/balance`, { headers: getHeaders() });
    setBalanceData(data);
  }, []);

  const fetchTransactions = useCallback(async (page = 1) => {
    const { data } = await axios.get(`${API}/api/billing/balance-transactions?page=${page}&limit=20`, { headers: getHeaders() });
    setTransactions(data.data || []);
    setTxTotal(data.total || 0);
  }, []);

  const fetchFeeLedger = useCallback(async () => {
    const { data } = await axios.get(`${API}/api/billing/fee-ledger?limit=50`, { headers: getHeaders() });
    setFeeLedger(data.data || data || []);
  }, []);

  const fetchPlans = useCallback(async () => {
    const [plansRes, subRes] = await Promise.all([
      axios.get(`${API}/api/plans/public`, { headers: getHeaders() }),
      axios.get(`${API}/api/billing/subscription`, { headers: getHeaders() }).catch(() => ({ data: null })),
    ]);
    setPlans(plansRes.data || []);
    setCurrentPlan(subRes.data);
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([fetchBalance(), fetchTransactions(), fetchFeeLedger(), fetchPlans()]);
  }, [fetchBalance, fetchTransactions, fetchFeeLedger, fetchPlans]);

  useEffect(() => {
    setLoading(true);
    refreshAll().finally(() => setLoading(false));
  }, [refreshAll]);

  if (loading) {
    return (
      <MerchantLayout>
        <div className="min-h-screen bg-background flex items-center justify-center">
          <RefreshCw className="h-6 w-6 text-muted-foreground animate-spin" />
        </div>
      </MerchantLayout>
    );
  }

  const balance = balanceData?.balance ?? 0;
  const inTrial = balanceData?.in_trial ?? false;
  const trialDaysLeft = balanceData?.trial_days_left ?? 0;
  const trialEnd = balanceData?.trial_end;
  const planFees = balanceData?.plan_fees;
  const currentPlanId = currentPlan?.plan_id || balanceData?.subscription?.plan_id;
  const currentPlanObj = plans.find(p => p.id === currentPlanId);
  const isPaidPlan = (planFees?.monthly_price ?? 0) > 0;
  const autoCharge = balanceData?.subscription?.auto_charge_monthly ?? false;

  const toggleAutoCharge = async (val) => {
    try {
      await axios.put(`${API}/api/billing/auto-charge`, { enabled: val }, { headers: getHeaders() });
      toast.success(val ? 'Auto-charge enabled. Monthly fee will be deducted automatically.' : 'Auto-charge disabled.');
      await fetchBalance();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update setting');
    }
  };

  return (
    <MerchantLayout>
      <div className="min-h-screen bg-background p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Billing</h1>
            <p className="text-sm text-muted-foreground">Prepaid account balance — charges deducted automatically</p>
          </div>
          <Button variant="outline" size="sm" onClick={refreshAll} className="gap-2">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-4 w-full max-w-lg">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="transactions">Transactions</TabsTrigger>
            <TabsTrigger value="fee-ledger">Fee Ledger</TabsTrigger>
            <TabsTrigger value="plans">Plans</TabsTrigger>
          </TabsList>

          {/* ── Overview ── */}
          <TabsContent value="overview" className="space-y-4 mt-5">

            {/* Auto-charge WARNING banner — shown at TOP when disabled */}
            {isPaidPlan && !autoCharge && (
              <div className="rounded-2xl border-2 border-amber-500 bg-amber-500/15 p-5" data-testid="auto-charge-warning-banner">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="h-5 w-5 shrink-0 text-amber-500" />
                      <h3 className="font-bold text-foreground text-sm">Auto-Charge is Disabled</h3>
                      <span data-testid="auto-charge-status-badge" className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500 text-white">
                        DISABLED
                      </span>
                    </div>
                    <p className="text-sm text-foreground leading-relaxed font-medium">
                      Your monthly plan fee of <span className="font-bold">${planFees?.monthly_price} USDT</span> will <span className="underline">NOT</span> be deducted automatically.
                    </p>
                    <p className="text-xs text-foreground/70 mt-1 leading-relaxed">
                      You must manually renew your plan before the billing date to avoid service interruption.
                    </p>
                  </div>
                  <div className="shrink-0 flex flex-col items-center gap-1.5">
                    <button
                      data-testid="auto-charge-toggle"
                      onClick={() => toggleAutoCharge(true)}
                      className="relative w-14 h-7 rounded-full bg-slate-400 border-2 border-slate-500 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2"
                      role="switch"
                      aria-checked={false}
                    >
                      <span className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-md transition-transform duration-200 translate-x-0" />
                    </button>
                    <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">OFF</span>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-amber-500/30">
                  <button
                    onClick={() => toggleAutoCharge(true)}
                    className="text-xs font-semibold text-amber-700 dark:text-amber-400 hover:underline flex items-center gap-1"
                  >
                    <CheckCircle className="h-3.5 w-3.5" />
                    Enable auto-charge now to ensure uninterrupted service
                  </button>
                </div>
              </div>
            )}

            {/* Current Plan — prominent hero */}
            <PlanHero
              subscription={balanceData?.subscription}
              planFees={planFees}
              inTrial={inTrial}
              trialDaysLeft={trialDaysLeft}
              trialEnd={trialEnd}
              onActivateEarly={() => setActivateEarlyOpen(true)}
              onSwitchPlan={() => setSwitchPlanOpen(true)}
            />

            {/* Balance */}
            <BalanceCard
              balance={balance}
              planExpired={balanceData?.plan_expired}
              graceEnd={balanceData?.grace_end}
              inTrial={inTrial}
              signupBonusPending={balanceData?.signup_bonus_pending}
              onAddBalance={() => setAddBalanceOpen(true)}
            />

            {/* Auto-charge toggle — shown in current position only when ENABLED */}
            {isPaidPlan && autoCharge && (
              <div className="rounded-2xl border-2 border-emerald-500 bg-emerald-500/10 p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="h-5 w-5 shrink-0 text-emerald-500" />
                      <h3 className="font-bold text-foreground text-sm">Auto-Charge Monthly Plan Fee</h3>
                      <span data-testid="auto-charge-status-badge" className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500 text-white">
                        ENABLED
                      </span>
                    </div>
                    <p className="text-sm text-foreground leading-relaxed">
                      Your monthly plan fee of <span className="font-bold">${planFees?.monthly_price} USDT</span> will be automatically deducted from your account balance on each renewal date.
                    </p>
                  </div>
                  <div className="shrink-0 flex flex-col items-center gap-1.5">
                    <button
                      data-testid="auto-charge-toggle"
                      onClick={() => toggleAutoCharge(false)}
                      className="relative w-14 h-7 rounded-full bg-emerald-500 border-2 border-emerald-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
                      role="switch"
                      aria-checked={true}
                    >
                      <span className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-md transition-transform duration-200 translate-x-7" />
                    </button>
                    <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">ON</span>
                  </div>
                </div>
              </div>
            )}

            {/* How charges work */}
            <div className="rounded-xl border border-border bg-card p-5 space-y-4">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-muted-foreground" /> How Charges Work
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  {
                    icon: <TrendingDown className="h-5 w-5 text-amber-400" />,
                    title: '% Per Transaction',
                    desc: `${planFees?.percent_fee ?? 0}% of each verified payment is deducted from your balance automatically.`,
                    color: 'border-amber-500/20 bg-amber-500/5',
                  },
                  {
                    icon: <Calendar className="h-5 w-5 text-blue-400" />,
                    title: 'Monthly Plan Fee',
                    desc: `$${planFees?.monthly_price ?? 0} USDT deducted on your renewal date. Ensure sufficient balance.`,
                    color: 'border-blue-500/20 bg-blue-500/5',
                  },
                  {
                    icon: <Zap className="h-5 w-5 text-purple-400" />,
                    title: 'Extra TX Fee',
                    desc: `$${planFees?.extra_tx_cost ?? 0} per transaction over your plan limit${planFees?.tx_limit ? ` (limit: ${planFees.tx_limit} TX/mo)` : ''}.`,
                    color: 'border-purple-500/20 bg-purple-500/5',
                  },
                ].map((item, i) => (
                  <div key={i} className={`p-4 rounded-xl border ${item.color} space-y-2`}>
                    {item.icon}
                    <p className="text-sm font-semibold text-foreground">{item.title}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent activity */}
            {transactions.length > 0 && (
              <div className="rounded-xl border border-border bg-card p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-foreground">Recent Activity</h3>
                  <button onClick={() => setActiveTab('transactions')} className="text-xs text-primary flex items-center gap-1 hover:underline">
                    View all <ChevronRight className="h-3 w-3" />
                  </button>
                </div>
                <div className="space-y-2">
                  {transactions.slice(0, 5).map(tx => {
                    const isCredit = tx.amount > 0;
                    return (
                      <div key={tx.id} className="flex items-center justify-between py-2 border-b border-border/40 last:border-0">
                        <div>
                          <p className="text-sm text-foreground">{tx.description}</p>
                          <p className="text-xs text-muted-foreground">{fmtDateTime(tx.created_at)}</p>
                        </div>
                        <span className={`text-sm font-semibold tabular-nums ${isCredit ? 'text-emerald-400' : 'text-red-400'}`}>
                          {isCredit ? '+' : ''}{fmt(tx.amount, 4)} USDT
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </TabsContent>

          {/* ── Transactions ── */}
          <TabsContent value="transactions" className="mt-5">
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">Balance Transactions</h3>
                  <p className="text-xs text-muted-foreground">{txTotal} total — top-ups and charges</p>
                </div>
                <Button size="sm" onClick={() => setAddBalanceOpen(true)} className="bg-emerald-500 hover:bg-emerald-600 text-white gap-1.5 text-xs">
                  <Plus className="h-3.5 w-3.5" />Add Balance
                </Button>
              </div>
              {transactions.length === 0 ? (
                <div className="p-12 text-center text-muted-foreground">
                  <Wallet className="h-8 w-8 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No transactions yet</p>
                  <p className="text-xs mt-1">Add balance to get started</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground">Type</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground">Description</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground">Date</th>
                        <th className="text-right py-3 px-4 text-xs font-medium text-muted-foreground">Amount</th>
                        <th className="text-right py-3 px-4 text-xs font-medium text-muted-foreground">Balance After</th>
                      </tr>
                    </thead>
                    <tbody>{transactions.map(tx => <TransactionRow key={tx.id} tx={tx} />)}</tbody>
                  </table>
                </div>
              )}
              {txTotal > 20 && (
                <div className="p-4 border-t border-border flex items-center justify-between">
                  <Button variant="outline" size="sm" onClick={() => { setTxPage(p => Math.max(1, p-1)); fetchTransactions(txPage - 1); }} disabled={txPage === 1}>Previous</Button>
                  <span className="text-xs text-muted-foreground">Page {txPage} of {Math.ceil(txTotal / 20)}</span>
                  <Button variant="outline" size="sm" onClick={() => { setTxPage(p => p+1); fetchTransactions(txPage + 1); }} disabled={txPage >= Math.ceil(txTotal / 20)}>Next</Button>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ── Fee Ledger ── */}
          <TabsContent value="fee-ledger" className="mt-5">
            <div className="rounded-xl border border-border bg-card overflow-hidden">
              <div className="p-4 border-b border-border">
                <h3 className="text-sm font-semibold text-foreground">Fee Ledger</h3>
                <p className="text-xs text-muted-foreground">Per-transaction fee breakdown</p>
              </div>
              {feeLedger.length === 0 ? (
                <div className="p-12 text-center text-muted-foreground">
                  <BarChart3 className="h-8 w-8 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No fee records yet</p>
                </div>
              ) : (() => {
                const hasExtraTx = feeLedger.some(entry => (entry.extra_tx_cost || 0) > 0);
                return (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border bg-muted/30">
                          <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground">Date</th>
                          <th className="text-left py-3 px-4 text-xs font-medium text-muted-foreground">Plan</th>
                          <th className="text-right py-3 px-4 text-xs font-medium text-muted-foreground">TX Amount</th>
                          <th className="text-right py-3 px-4 text-xs font-medium text-muted-foreground">% Fee</th>
                          {hasExtraTx && (
                            <th className="text-right py-3 px-4 text-xs font-medium text-muted-foreground">TX Cost</th>
                          )}
                          <th className="text-right py-3 px-4 text-xs font-medium text-muted-foreground">Total Fee</th>
                        </tr>
                      </thead>
                      <tbody>
                        {feeLedger.map(entry => (
                          <tr key={entry.id} className="border-b border-border/50 hover:bg-muted/30">
                            <td className="py-3 px-4 text-muted-foreground text-xs">{fmtDateTime(entry.created_at)}</td>
                            <td className="py-3 px-4"><Badge variant="outline" className="text-xs">{entry.plan_name}</Badge></td>
                            <td className="py-3 px-4 text-right font-mono text-xs">${fmt(entry.tx_amount, 4)}</td>
                            <td className="py-3 px-4 text-right text-amber-400 font-mono text-xs">${fmt(entry.percent_fee_amount, 6)}</td>
                            {hasExtraTx && (
                              <td className="py-3 px-4 text-right text-purple-400 font-mono text-xs">
                                {(entry.extra_tx_cost || 0) > 0 ? `$${fmt(entry.extra_tx_cost, 4)}` : '—'}
                              </td>
                            )}
                            <td className="py-3 px-4 text-right text-red-400 font-semibold font-mono text-xs">${fmt(entry.total_fee, 6)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </div>
          </TabsContent>

          {/* ── Plans ── */}
          <TabsContent value="plans" className="mt-5">
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-foreground">Available Plans</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Switch plans anytime. Charges are immediate for paid plans.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {plans.map(plan => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  isCurrent={plan.id === currentPlanId}
                  inTrial={inTrial && plan.id === currentPlanId}
                  onSwitch={(planId) => { setPreselectedPlanId(planId); setSwitchPlanOpen(true); }}
                />
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Modals */}
        <AddBalanceModal
          open={addBalanceOpen}
          onClose={() => setAddBalanceOpen(false)}
          onSuccess={refreshAll}
        />
        <ActivateEarlyModal
          open={activateEarlyOpen}
          onClose={() => setActivateEarlyOpen(false)}
          plan={currentPlanObj}
          balance={balance}
          onSuccess={refreshAll}
          onAddBalance={() => { setActivateEarlyOpen(false); setAddBalanceOpen(true); }}
        />
        <SwitchPlanModal
          open={switchPlanOpen}
          onClose={() => { setSwitchPlanOpen(false); setPreselectedPlanId(null); }}
          plans={plans}
          currentPlanId={currentPlanId}
          preselectedPlanId={preselectedPlanId}
          balance={balance}
          onSuccess={refreshAll}
          onAddBalance={() => { setSwitchPlanOpen(false); setAddBalanceOpen(true); }}
        />
      </div>
    </MerchantLayout>
  );
}
